import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatTime, formatDuration, TIME_FORMATS } from '../../../../utils/timeUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrainingScenes, type TrainingSceneInfo, TrainingStatus } from '@/lib/api/ai-training/scene';
import { formatTextWithLineBreaks } from '../../../../utils/textUtils';


// 导入图片资源
import logo3 from '@/assets/images/ai/logo3.png';

// 组件导入
import ScoreRadar from '@/pages/ai-training/analysis/components/ScoreRadar';
import DimensionModal from '@/pages/ai-training/analysis/components/DimensionModal';
import AudioPlayer from '@/components/AudioPlayer/AudioPlayer';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import PCMaxWidthContainer from '@/components/Layout/PCMaxWidthContainer';
import tongguojiangpai from '@/assets/images/dudao/tongguojiangpai.png'
import weitongguojiangpai from '@/assets/images/dudao/weitongguojiangpai.png'
import jiangbei from '@/assets/images/dudao/jiangbei.png'

interface TotalInfo {
  label: string;
  score: number;
  total: number;
  comment: string;
}

interface ScoringCriteria {
  name: string;
  value: string;
}

interface AnalysisResultProps {
  apiData: any;
  onBack?: () => void;
  embedded?: boolean;
  agentId?: string | null;
}

const AnalysisResult: React.FC<AnalysisResultProps> = ({ apiData, onBack, embedded = false, agentId }) => {
  const [showDimensionDesc, setShowDimensionDesc] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'suggestions'>('overview');
  const [trainingScenes, setTrainingScenes] = useState<TrainingSceneInfo[]>([]);
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const navigate = useNavigate();

  // 当有 agentId 时，不显示 tabs，直接显示整体分析概览
  const shouldShowTabs = !agentId;

  // 获取推荐的AI陪练场景
  useEffect(() => {
    const fetchTrainingScenes = async () => {
      try {
        const response = await getTrainingScenes({
          time_status: TrainingStatus.IN_PROGRESS,
          page: 1,
          pageSize: 3 // 只获取3个推荐
        });
        if (response.success && response.data) {
          setTrainingScenes(response.data.list);
        }
      } catch (error) {
        console.error('获取推荐AI陪练失败:', error);
      }
    };

    if (activeTab === 'suggestions') {
      fetchTrainingScenes();
    }
  }, [activeTab]);
  // 从API数据中提取维度得分信息
  const getTotalInfo = (): TotalInfo[] => {
    if (!apiData?.analysis_result?.dimension_scores) return [];

    const dimensionScores = apiData.analysis_result.dimension_scores as Record<string, any>;
    const dimensionsOrder: string[] = (
      apiData?.scenario_data?.scoring_criteria_config?.criteria_data?.dimensions || []
    ).map((dim: any) => dim?.name).filter(Boolean);

    const orderMap = new Map<string, number>(
      dimensionsOrder.map((name: string, idx: number) => [name, idx])
    );

    const entries = Object.entries(dimensionScores) as [string, any][];

    entries.sort((a, b) => {
      const ai = orderMap.has(a[0]) ? (orderMap.get(a[0]) as number) : Number.MAX_SAFE_INTEGER;
      const bi = orderMap.has(b[0]) ? (orderMap.get(b[0]) as number) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });

    return entries.map(([key, value]) => ({
      label: key,
      score: value?.score ?? 0,
      total: value?.total ?? 100,
      comment: value?.feedback ?? '暂无评价'
    }));
  };

  // 从API数据中提取评分标准
  const getScoringCriteria = (): ScoringCriteria[] => {
    if (!apiData?.scenario_data?.scoring_criteria_config?.criteria_data?.dimensions)
      return [];

    const dimensions = apiData.scenario_data.scoring_criteria_config.criteria_data.dimensions;
    return dimensions.map((dim: any) => ({
      name: dim.name,
      value: dim.scoring_rule || '暂无描述'
    }));
  };

  // 计算动态数据
  const totalInfo = getTotalInfo();
  const scoringCriteria = getScoringCriteria();

  // 计算通过状态
  const passingScore = apiData?.scenario_data?.passing_score || 60;
  const isPassed = (apiData?.overall_score || 0) >= passingScore;

  // 查看分析记录
  const handleViewRecords = () => {
  
    navigate(`/ai-supervisor/records/${apiData?.scenario_id}`);
  };

  // 查看排行榜
  const handleViewRank = () => {
    navigate(`/ai-supervisor/rank-list/${apiData?.scenario_id}`);
  };

 // 再次上传
 const handlePracticeAgain = () => {
  console.log('apiData', apiData);
  const scenarioId = apiData?.scenario_id || '';
  navigate(`/ai-supervisor/detail/${scenarioId}`);
};

  return (
    <PCMaxWidthContainer bgGradient='bg-white'>
    <div className='bg-white'>
      {/* 主体内容 */}
      <div className='px-[15px] pt-4 pb-[100px]'>
        {/* 返回按钮 - 只在 embedded 为 true 时显示 */}
        {embedded && (
          <div className='mb-4'>
            <button
              onClick={() => navigate(-1)}
              className='flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors'
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="#303133" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}

        {/* 场景名称 */}
        {/* <div className='mb-[18px]'>
          <p className='text-[#303133] text-[16px] font-medium leading-[1.6]'>
            {apiData?.scenario_data?.name || 'AI督导分析'}
          </p>
        </div> */}

        {/* 音频/视频播放器卡片 */}
        {apiData?.content_data?.source_url && (
          <div className={`bg-[#f5f7fa] rounded-[12px] mb-[18px] px-[15px] ${apiData.content_data.content_type === 'video' ? 'py-[15px]' : 'h-[96px] flex flex-col justify-center'}`}>
            <p className='text-[#86909c] text-[12px] leading-[1.6] text-center mb-[12px]'>
              {apiData.content_data.file_name || (apiData.content_data.content_type === 'video' ? '视频名称' : '音频名称')}
            </p>
            {apiData.content_data.content_type === 'video' ? (
              <VideoPlayer
                videoUrl={apiData.content_data.source_url}
                fileName={apiData.content_data.file_name || '视频文件'}
                compact={true}
              />
            ) : (
              <AudioPlayer
                audioUrl={apiData.content_data.source_url}
                fileName={apiData.content_data.file_name || '音频文件'}
                compact={true}
              />
            )}
          </div>
        )}

        {/* 用户信息卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className='relative bg-gradient-to-b from-white to-[rgba(255,255,255,0.5)] border border-white rounded-[8px] mb-[18px] '
        >
          {/* 背景图片 */}
          <div className='absolute inset-0 opacity-50'>
            <img
            style={{
              background: 'linear-gradient(262deg, #E6F4FF -0.92%, #F9F0FF 99.04%)'
            }}
              alt=""
              className='w-full h-full object-cover rotate-180 scale-y-[-1]'
            />
          </div>

          <div className='relative px-4 py-[18px] flex items-start justify-between'>
            {/* 左侧：头像和信息 */}
            <div className='flex items-start gap-3'>
              {/* 头像 */}
              <div className='w-12 h-12 rounded-[6px] border border-white overflow-hidden flex-shrink-0'>
                <img
                  src={apiData?.user_avatar || "https://www.figma.com/api/mcp/asset/f562bd2b-1ea0-41dd-8f59-47f8683d0207"}
                  alt=""
                  className='w-full h-full object-cover'
                />
              </div>

              {/* 用户信息 */}
              <div>
                <p className='text-[#0a1b39] text-[14px] leading-[22px] mb-[4px]'>
                  姓名：{apiData?.user_full_name || apiData?.user_name || '待验证'}
                </p>
                <p className='text-[#86909c] text-[12px] leading-[1.6]'>
                  {formatTime(apiData?.created_at || '', TIME_FORMATS.DATETIME_MINUTE, true)}
                </p>
              </div>
            </div>

            {/* 右侧：成绩徽章 */}
            <div className='absolute top-0 right-3 -mt-3 flex items-center justify-center'>
              <img
                className='w-20 h-20 rounded-3xl'
                src={isPassed ? tongguojiangpai : weitongguojiangpai}
                alt=''
              />
              <div
                className={`absolute top-5 left-0 w-20 text-xl flex items-end justify-center ${
                  isPassed
                    ? 'text-[#F9B43D]'
                    : 'text-[#989CA3]'
                }`}
              >
                {apiData?.overall_score || 0}
              </div>
              <div
                className={`absolute top-11 left-0 w-20 text-xs flex items-end justify-center ${
                  isPassed
                    ? 'text-white'
                    : 'text-[#767779]'
                }`}
              >
                {isPassed ? '已通过' : '未通过'}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tab切换 - 只在没有 agentId 时显示 */}
        {shouldShowTabs && (
          <div className='flex items-center mb-[18px] border-b border-[#e5e7eb]'>
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 pb-3 text-[14px] font-medium transition-colors relative ${
                activeTab === 'overview'
                  ? 'text-[#5a6fff]'
                  : 'text-[#86909c]'
              }`}
            >
              整体分析概览
              {activeTab === 'overview' && (
                <div className='absolute bottom-0 left-0 right-0 h-[2px] bg-[#5a6fff]'></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('suggestions')}
              className={`flex-1 pb-3 text-[14px] font-medium transition-colors relative ${
                activeTab === 'suggestions'
                  ? 'text-[#5a6fff]'
                  : 'text-[#86909c]'
              }`}
            >
              个性化改进建议
              {activeTab === 'suggestions' && (
                <div className='absolute bottom-0 left-0 right-0 h-[2px] bg-[#5a6fff]'></div>
              )}
            </button>
          </div>
        )}

        {/* Tab内容区域 */}
        {(shouldShowTabs ? activeTab === 'overview' : true) ? (
          <div>
            {/* 各维度得分 */}
            <div className='mb-[18px]'>
              <div className='flex items-center'>
                <span className='text-[#181c1f] text-[16px] font-medium leading-[24px]'>
                  各维度得分
                </span>
              </div>
              <div
                className='h-[8px] w-[78px] mb-4'
                style={{
                  backgroundImage: 'linear-gradient(-86.24946639307015deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                }}
              ></div>
              <ScoreRadar totalInfo={totalInfo} />
            </div>

            {/* 总结 */}
            <div className='mb-[18px]'>
              <div className='flex items-center'>
                <span className='text-[#0a1b39] text-[16px] font-medium leading-[24px]'>
                  总结
                </span>
              </div>
              <div
                className='h-[8px] w-[32px] mb-4'
                style={{
                  backgroundImage: 'linear-gradient(-88.45948939854074deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                }}
              ></div>
              <div className='text-[#86909c] text-[14px] leading-[1.6]'>
                <div dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(apiData?.analysis_result?.comprehensive_feedback || '暂无综合评价') }} />
              </div>
            </div>

            {/* 各项数据 */}
            {totalInfo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className='mb-[18px]'
              >
                <div className='flex items-center'>
                  <span className='text-[#0a1b39] text-[16px] font-medium leading-[24px]'>
                    {item.label}
                    <span className='text-[#998dff] ml-1'>（{item.score}/{item.total}）</span>
                  </span>
                </div>
                <div
                  className='h-[8px] mb-4'
                  style={{
                    width: `${Math.min(item.label.length * 16 + 50, 200)}px`,
                    backgroundImage: 'linear-gradient(-82.67286391533281deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                  }}
                ></div>
                <div className='text-[#86909c] text-[14px] leading-[1.6]'>
                  <div dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(item.comment) }} />
                </div>
              </motion.div>
            ))}
          </div>
        ) : shouldShowTabs ? (
          <div>
            {/* 改进建议 */}
            {apiData?.analysis_result_golden ? (
              <div>
                {/* 改进建议 */}
                {apiData.analysis_result_golden.suggestion && (
                  <div className='mb-[18px]'>
                    <div className='flex items-center'>
                      <span className='text-[#0a1b39] text-[16px] font-medium leading-[24px]'>
                        改进建议
                      </span>
                    </div>
                    <div
                      className='h-[8px] w-[64px] mb-4'
                      style={{
                        backgroundImage: 'linear-gradient(-82.67286391533281deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                      }}
                    ></div>
                    <div className='text-[#86909c] text-[14px] leading-[1.6]'>
                      <div dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(apiData.analysis_result_golden.suggestion) }} />
                    </div>
                  </div>
                )}

                {/* 金牌话术推荐 */}
                {apiData.analysis_result_golden.golden_dialogues && (
                  <div className='mb-[18px]'>
                    <div className='flex items-center'>
                      <span className='text-[#0a1b39] text-[16px] font-medium leading-[24px]'>
                        金牌话术推荐
                      </span>
                    </div>
                    <div
                      className='h-[8px] w-[96px] mb-4'
                      style={{
                        backgroundImage: 'linear-gradient(-82.67286391533281deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                      }}
                    ></div>
                    <div className='bg-[#f5f7fa] rounded-[8px] p-4'>
                      <div className='text-[#86909c] text-[14px] leading-[1.6]'>
                        <div dangerouslySetInnerHTML={{ __html: formatTextWithLineBreaks(apiData.analysis_result_golden.golden_dialogues) }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* 推荐课程 */}
                {/* <div className='mb-[18px]'>
                  <div className='flex items-center'>
                    <span className='text-[#0a1b39] text-[16px] font-medium leading-[24px]'>
                      推荐课程
                    </span>
                  </div>
                  <div
                    className='h-[8px] w-[64px] mb-4'
                    style={{
                      backgroundImage: 'linear-gradient(-82.67286391533281deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                    }}
                  ></div>
                  <div
                    className='bg-white rounded-[12px] flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow'
                    onClick={() => navigate('/ai-training/list')}
                  >
                    <div className='h-16 w-20 rounded-[8px] overflow-hidden flex-shrink-0'>
                      <img
                        src="https://www.figma.com/api/mcp/asset/6f72353c-9867-48bb-8708-203313934af8"
                        alt="课程封面"
                        className='w-full h-full object-cover'
                      />
                    </div>
                    <div className='flex-1'>
                      <p className='text-[#0a1b39] text-[14px] font-medium mb-1'>
                        高情商沟通话术
                      </p>
                      <p className='text-[#86909c] text-[12px] leading-[1.6]'>
                        化繁为简练沟通，卓越细节得要领
                      </p>
                    </div>
                  </div>
                </div> */}

                {/* 推荐AI陪练 - 只在 embedded 为 false 时显示 */}
                {!embedded && (
                  <div className='mb-[18px]'>
                    <div className='flex items-center'>
                      <span className='text-[#0a1b39] text-[16px] font-medium leading-[24px]'>
                        推荐AI陪练
                      </span>
                    </div>
                    <div
                      className='h-[8px] w-[88px] mb-4'
                      style={{
                        backgroundImage: 'linear-gradient(-82.67286391533281deg, rgba(230, 244, 255, 1) 0.33138%, rgba(249, 240, 255, 1) 99.664%)'
                      }}
                    ></div>
                    {trainingScenes.length > 0 ? (
                      <div className='space-y-3'>
                        {trainingScenes.map((scene) => (
                          <div
                            key={scene.id}
                            className='bg-white rounded-[12px]  cursor-pointer hover:shadow-md transition-shadow'
                            onClick={() => navigate(`/ai-training/detail/${scene.scenario_id}`)}
                          >
                            <div className='flex items-center gap-3'>
                              <div className=' h-16 rounded-[8px] overflow-hidden flex-shrink-0'>
                                <img
                                  src={scene.scenario_cover || 'https://www.figma.com/api/mcp/asset/6f72353c-9867-48bb-8708-203313934af8'}
                                  alt={scene.scenario_name}
                                  className='w-full h-full object-cover'
                                />
                              </div>
                              <div className='flex-1'>
                                <p className='text-[#0a1b39] text-[14px] font-medium mb-1 truncate'>
                                  {scene.scenario_name}
                                </p>
                                <p className='text-[#86909c] text-[12px] leading-[1.6] line-clamp-2'>
                                  {scene.assign_description || '专业的AI陪练场景'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className='text-[#86909c] text-[14px] leading-[1.6]'>
                        暂无推荐的AI陪练
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className='text-center py-10 text-[#86909c]'>
                暂无改进建议
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* 底部操作按钮 */}
      <div className='fixed left-0 right-0 bottom-0 bg-white h-[78px] px-[15px] flex items-center gap-[10px] shadow-[0px_-7px_11.2px_0px_rgba(0,0,0,0.06)]'>
        {/* 历史记录按钮 */}
        <button
        onClick={handleViewRank}
          className='w-[46px] h-[47px] rounded-full bg-white backdrop-blur-[2px] flex items-center justify-center flex-shrink-0'
          style={{
            boxShadow: '0px 0px 13.6px -1px rgba(53,14,145,0.08)'
          }}
        >
          <img
            src={jiangbei}
            alt=""
            className='w-[30px]'
          />
        </button>

        {/* 分析记录按钮 */}
        <button
        onClick={handleViewRecords}
          className='flex-1 h-[46px] rounded-[39px] border border-[#64adff] border-solid bg-[rgba(255,255,255,0.55)] backdrop-blur-[2px] flex items-center justify-center min-w-0'
          style={{
            boxShadow: '0px 0px 13.6px -1px rgba(53,14,145,0.08)'
          }}
        >
          <span
            className='text-[14px] leading-[22px] font-medium'
            style={{
              background: 'linear-gradient(90deg, #64adff 0%, #5a6fff 50%, #998dff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            分析记录
          </span>
        </button>

        {/* 再次上传按钮 */}
        <button
        onClick={handlePracticeAgain}
          className='flex-1 h-[46px] rounded-[39px] flex items-center justify-center relative overflow-hidden min-w-0'
          style={{
            background: 'linear-gradient(90deg, #64adff 0%, #5a6fff 50%, #998dff 100%)',
            boxShadow: '0px 0px 13.6px -1px rgba(31,6,90,0.08)'
          }}
        >
          <span className='text-white text-[14px] leading-[22px] font-medium relative z-10'>
            再次上传
          </span>
        </button>
      </div>
    </div>
    </PCMaxWidthContainer>
  );
};

export default AnalysisResult;
